"use client";

import { useState } from "react";
import { Check, Copy, Link2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import Button from "@/components/ui/Button";
import Modal from "@/components/ui/Modal";

export type CompareShareUrlBoxProps =
  | {
      variant: "full";
      shareUrl: string;
      listingsCount: number;
      /** Скрывает текст-подводку над кнопками (например, она перенесена в tooltip страницы) */
      compact?: boolean;
    }
  | {
      variant: "placeholder";
      previewDisplayed: string;
      clipboardText: string;
    };

export default function CompareShareUrlBox(props: CompareShareUrlBoxProps) {
  const t = useTranslations("ComparePage");
  const [modalOpen, setModalOpen] = useState(false);
  const [copiedBurst, setCopiedBurst] = useState(false);

  const toCopy = props.variant === "full" ? props.shareUrl : props.clipboardText;
  const panelUrlPreview = props.variant === "full" ? props.shareUrl : props.previewDisplayed;

  async function copyUrl() {
    try {
      await navigator.clipboard.writeText(toCopy);
      toast.success(t("copySuccess"));
      setCopiedBurst(true);
      window.setTimeout(() => setCopiedBurst(false), 2000);
    } catch {
      toast.error(t("copyFail"));
    }
  }

  const chipLabel = props.variant === "full" ? t("shareChipFull") : t("shareChipPlaceholder");
  const compactFull = props.variant === "full" && props.compact;
  const inlineLeadFull =
    props.variant === "full" && !compactFull ? t("shareTooltipFullLead", { count: props.listingsCount }) : null;
  const placeholderPanelLead =
    props.variant === "placeholder" ? t("shareTooltipPlaceholderLead") : null;
  const modalTitle =
    props.variant === "full" ? t("shareModalTitleFull") : t("shareModalTitlePlaceholder");
  const isPlaceholder = props.variant === "placeholder";

  return (
    <>
      <div
        className={cn(
          "gap-2",
          isPlaceholder ? "inline-flex flex-wrap items-center" : compactFull ? "inline-flex flex-wrap items-center" : "flex flex-col flex-wrap items-start",
        )}
      >
        {inlineLeadFull ? (
          <p className="m-0 max-w-xl text-pretty text-xs leading-relaxed text-gray-600 dark:text-gray-400">{inlineLeadFull}</p>
        ) : null}

        <div className="flex shrink-0 flex-wrap items-center gap-1.5">
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-800 shadow-sm transition-colors hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-200 dark:hover:border-gray-600 dark:hover:bg-gray-900"
            aria-haspopup="dialog"
            aria-expanded={modalOpen}
            aria-label={t("shareChipAria")}
            onClick={() => setModalOpen(true)}
          >
            <Link2 size={14} className="shrink-0 text-gray-500 dark:text-gray-400" aria-hidden />
            <span
              className={cn(isPlaceholder ? "max-w-[8.25rem] truncate sm:max-w-none" : "")}
              title={chipLabel}
            >
              {chipLabel}
            </span>
          </button>
          <button
            type="button"
            onClick={copyUrl}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-400 dark:hover:bg-gray-900 dark:hover:text-gray-100"
            title={t("copyLinkTitle")}
            aria-label={t("copyLinkAria")}
          >
            {copiedBurst ? <Check size={16} className="text-emerald-600" aria-hidden /> : <Copy size={16} aria-hidden />}
          </button>
        </div>
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={modalTitle} size="md" closeOnBackdrop>
        <div className="flex flex-col gap-4">
          {props.variant === "placeholder" && placeholderPanelLead ? (
            <p className="m-0 text-pretty text-sm leading-relaxed text-gray-600 dark:text-gray-400">{placeholderPanelLead}</p>
          ) : null}
          <pre className="m-0 max-h-40 select-all overflow-auto whitespace-pre-wrap break-all rounded-xl bg-gray-50 px-3 py-3 font-mono text-[11px] leading-snug text-gray-800 dark:bg-gray-800/80 dark:text-gray-200">
            {panelUrlPreview}
          </pre>
          <Button type="button" variant="outline" size="sm" className="w-full justify-center text-sm" onClick={copyUrl}>
            {copiedBurst ? <Check size={14} className="mr-1.5 inline" aria-hidden /> : <Copy size={14} className="mr-1.5 inline" aria-hidden />}
            {props.variant === "full" ? t("copyComparisonLinkAction") : t("copyComparePageLinkAction")}
          </Button>
        </div>
      </Modal>
    </>
  );
}
